// src/pages/AdminPage.jsx — ADMIN COM GERENCIAMENTO DE SUPERVISOR
//
// Out/26 (blindagem contra banimento): esta é a tela do ADMIN da Cetto — a
// conta do fundador, sem empresa e por isso sem sino. É aqui que ele vê:
//   - os alertas da Meta de cada empresa (banimento, violação, restrição),
//     numa faixa no topo e numa coluna da tabela;
//   - a assinatura (em teste ou não).
// E é daqui que ele age, pelo menu "Proteção" de cada linha:
//   - Liberar temas: libera um tema de risco (crédito, apostas…) para UMA
//     empresa, depois de conferir que ela é autorizada e tem opt-in;
//   - Liberar disparos: fecha os alertas de conta da Meta depois de olhar
//     o caso (violação e restrição não têm aviso de "resolvido");
//   - Encerrar empresa: desliga da Meta, cancela a assinatura e apaga TUDO
//     na hora (services/encerramento). Sem volta — pede o nome digitado.
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Stack,
  Divider,
} from '@mui/material';
import AppSidebar from '../../components/AppSidebar';
import api from '../../services/Api';

const ASSINATURA = {
  TRIALING: { label: 'Em teste', color: 'warning' },
  ACTIVE: { label: 'Ativa', color: 'success' },
  PAST_DUE: { label: 'Pagamento atrasado', color: 'error' },
  CANCELED: { label: 'Cancelada', color: 'default' },
  PENDING: { label: 'Pendente', color: 'default' },
  INACTIVE: { label: 'Inativa', color: 'default' },
};

// Mesma comparação do servidor (admin.encerrar_empresa): sem diferença de
// maiúscula nem de espaços repetidos.
const normalizarNome = (s) => (s || '').split(/\s+/).filter(Boolean).join(' ').toLocaleLowerCase('pt-BR');

function formatarData(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '';
  }
}

export default function AdminPage() {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aviso, setAviso] = useState(null); // { severity, texto }

  // Modal de gerenciamento de supervisor
  const [openModal, setOpenModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [newSupervisorName, setNewSupervisorName] = useState('');
  const [newSupervisorEmail, setNewSupervisorEmail] = useState('');
  const [newSupervisorPassword, setNewSupervisorPassword] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalMsg, setModalMsg] = useState('');

  // Menu "Proteção" e seus diálogos
  const [menu, setMenu] = useState(null); // { anchor, company }
  const [temas, setTemas] = useState([]);
  const [temasDe, setTemasDe] = useState(null); // empresa
  const [temasMarcados, setTemasMarcados] = useState([]);
  const [salvandoTemas, setSalvandoTemas] = useState(false);
  const [encerrarDe, setEncerrarDe] = useState(null); // empresa
  const [confirmacao, setConfirmacao] = useState('');
  const [motivo, setMotivo] = useState('');
  const [encerrando, setEncerrando] = useState(false);
  const [erroEncerrar, setErroEncerrar] = useState('');
  const [resumo, setResumo] = useState(null);

  useEffect(() => {
    fetchCompanies();
    api.get('/admin/temas', auth).then((res) => setTemas(res.data || [])).catch(() => setTemas([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/admin/companies', auth);
      setCompanies(res.data);
      setLoading(false);
    } catch (err) {
      alert('Erro ao carregar empresas');
      setLoading(false);
    }
  };

  const comAlerta = companies.filter((c) => (c.alertas_meta || []).length > 0);

  const handleOpenModal = (company) => {
    setSelectedCompany(company);
    setNewSupervisorName('');
    setNewSupervisorEmail('');
    setNewSupervisorPassword('');
    setModalMsg('');
    setOpenModal(true);
  };

  const handleAssignSupervisor = async () => {
    if (!newSupervisorName || !newSupervisorEmail || !newSupervisorPassword) {
      setModalMsg('Preencha todos os campos');
      return;
    }

    setModalLoading(true);
    setModalMsg('');

    try {
      // Cria o novo supervisor e atribui à empresa
      await api.post('/admin/assign-supervisor', {
        company_id: selectedCompany.id,
        name: newSupervisorName,
        email: newSupervisorEmail,
        password: newSupervisorPassword
      }, auth);

      setModalMsg('Supervisor atribuído com sucesso!');
      fetchCompanies(); // atualiza a tabela
      setTimeout(() => setOpenModal(false), 1500);
    } catch (err) {
      setModalMsg(err.response?.data?.detail || 'Erro ao atribuir supervisor');
    } finally {
      setModalLoading(false);
    }
  };

  // ─── Proteção ─────────────────────────────────────────────────────────

  const abrirTemas = (company) => {
    setMenu(null);
    setTemasDe(company);
    setTemasMarcados(company.temas_liberados || []);
  };

  const salvarTemas = async () => {
    setSalvandoTemas(true);
    try {
      await api.put(`/admin/companies/${temasDe.id}/temas`, { liberados: temasMarcados }, auth);
      setAviso({ severity: 'success', texto: `Temas de ${temasDe.name} atualizados.` });
      setTemasDe(null);
      fetchCompanies();
    } catch (err) {
      setAviso({ severity: 'error', texto: err.response?.data?.detail || 'Não foi possível salvar os temas.' });
    } finally {
      setSalvandoTemas(false);
    }
  };

  const liberarDisparos = async (company) => {
    setMenu(null);
    const pergunta = `Liberar os disparos em massa de ${company.name}? Os alertas da Meta desta empresa serão `
      + 'fechados. Faça isso só depois de conferir o caso no Gerenciador do WhatsApp.';
    if (!window.confirm(pergunta)) return;
    try {
      const res = await api.post(`/admin/companies/${company.id}/liberar-disparos`, {}, auth);
      const n = res.data?.fechados || 0;
      setAviso({
        severity: 'success',
        texto: n ? `Disparos de ${company.name} liberados (${n} alerta${n === 1 ? '' : 's'} fechado${n === 1 ? '' : 's'}).`
          : `${company.name} não tinha alerta aberto.`,
      });
      fetchCompanies();
    } catch (err) {
      setAviso({ severity: 'error', texto: err.response?.data?.detail || 'Não foi possível liberar os disparos.' });
    }
  };

  const abrirEncerrar = (company) => {
    setMenu(null);
    setEncerrarDe(company);
    setConfirmacao('');
    setMotivo('');
    setErroEncerrar('');
    setResumo(null);
  };

  const nomeConfere = encerrarDe && normalizarNome(confirmacao) === normalizarNome(encerrarDe.name);
  const podeEncerrar = nomeConfere && motivo.trim().length >= 3 && !encerrando;

  const encerrar = async () => {
    if (!podeEncerrar) return;
    setEncerrando(true);
    setErroEncerrar('');
    try {
      const res = await api.post(`/admin/companies/${encerrarDe.id}/encerrar`, {
        confirmacao, motivo: motivo.trim(),
      }, auth);
      setResumo(res.data);
      fetchCompanies();
    } catch (err) {
      setErroEncerrar(err.response?.data?.detail || 'O encerramento falhou e nenhum dado foi apagado.');
    } finally {
      setEncerrando(false);
    }
  };

  if (loading) return <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 10 }} />;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppSidebar user={user} />

      <Box sx={{ flexGrow: 1, ml: { xs: 0, md: '300px' }, p: { xs: 3, md: 6 }, bgcolor: '#f8fafc', minWidth: 0 }}>
        <Typography variant="h4" fontWeight="bold" color="#0f172a" mb={2}>
          Admin — Controle Geral
        </Typography>
        <Typography variant="body1" color="#64748b" mb={comAlerta.length ? 3 : 6}>
          Visão completa de todas as empresas no Cetto Systems
        </Typography>

        {comAlerta.length > 0 && (
          <Alert severity="error" sx={{ mb: 4, borderRadius: 3 }}>
            <Typography fontWeight={700} fontSize="0.9rem" mb={0.5}>
              {comAlerta.length === 1 ? '1 empresa com alerta da Meta' : `${comAlerta.length} empresas com alerta da Meta`}
              {' '}— os disparos em massa delas estão parados.
            </Typography>
            {comAlerta.map((c) => (
              <Typography key={c.id} fontSize="0.85rem">
                <strong>{c.name}</strong>: {c.alertas_meta.map((a) => `${a.rotulo}${a.desde ? ` (desde ${formatarData(a.desde)})` : ''}`).join(' · ')}
              </Typography>
            ))}
          </Alert>
        )}

        {aviso && (
          <Alert severity={aviso.severity} onClose={() => setAviso(null)} sx={{ mb: 3, borderRadius: 3 }}>
            {aviso.texto}
          </Alert>
        )}

        <Paper sx={{ borderRadius: 4, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                  <TableCell><strong>Empresa</strong></TableCell>
                  <TableCell><strong>Supervisor</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell align="center"><strong>Chips</strong></TableCell>
                  <TableCell align="center"><strong>Agentes</strong></TableCell>
                  <TableCell align="center"><strong>Assinatura</strong></TableCell>
                  <TableCell align="center"><strong>Meta</strong></TableCell>
                  <TableCell align="center"><strong>Ações</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies.map((company) => {
                  const assinatura = ASSINATURA[company.assinatura] || { label: company.assinatura || '—', color: 'default' };
                  const alertas = company.alertas_meta || [];
                  const liberados = company.temas_liberados || [];
                  return (
                    <TableRow key={company.id} hover>
                      <TableCell>
                        <strong>{company.name}</strong>
                        {liberados.length > 0 && (
                          <Typography fontSize="0.72rem" color="#b45309">
                            Temas liberados: {liberados.map((k) => temas.find((t) => t.chave === k)?.nome || k).join(', ')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {company.supervisor_name || (
                          <Chip label="Sem supervisor" color="error" size="small" />
                        )}
                      </TableCell>
                      <TableCell>{company.supervisor_email || '-'}</TableCell>
                      <TableCell align="center">{company.devices_count}</TableCell>
                      <TableCell align="center">{company.agents_count}</TableCell>
                      <TableCell align="center">
                        <Chip label={assinatura.label} color={assinatura.color} size="small" />
                        {company.plano && (
                          <Typography fontSize="0.7rem" color="#64748b" mt={0.3}>{company.plano}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {alertas.length ? (
                          <Stack spacing={0.5} alignItems="center">
                            {alertas.map((a) => <Chip key={a.rotulo} label={a.rotulo} color="error" size="small" />)}
                          </Stack>
                        ) : (
                          <Typography fontSize="0.8rem" color="#94a3b8">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Stack spacing={0.75} alignItems="stretch" sx={{ '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpenModal(company)}
                          >
                            Gerenciar Supervisor
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color={alertas.length ? 'error' : 'inherit'}
                            onClick={(e) => setMenu({ anchor: e.currentTarget, company })}
                            aria-haspopup="menu"
                          >
                            Proteção
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
      </Paper>

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => abrirTemas(menu.company)}>
          <ListItemText primary="Liberar temas" secondary="Crédito, apostas e outros temas de risco" />
        </MenuItem>
        <MenuItem onClick={() => liberarDisparos(menu.company)} disabled={!(menu?.company?.alertas_meta || []).length}>
          <ListItemText primary="Liberar disparos" secondary="Fecha os alertas da Meta desta empresa" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => abrirEncerrar(menu.company)} sx={{ color: 'error.main' }}>
          <ListItemText primary="Encerrar empresa" secondary="Desliga da Meta e apaga tudo, sem volta" />
        </MenuItem>
      </Menu>

      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Gerenciar Supervisor — {selectedCompany?.name}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Crie um novo supervisor para esta empresa
          </Typography>
          <TextField label="Nome" fullWidth value={newSupervisorName} onChange={e => setNewSupervisorName(e.target.value)} sx={{ mb: 2 }} />
          <TextField label="E-mail" type="email" fullWidth value={newSupervisorEmail} onChange={e => setNewSupervisorEmail(e.target.value)} sx={{ mb: 2 }} />
          <TextField label="Senha" type="password" fullWidth value={newSupervisorPassword} onChange={e => setNewSupervisorPassword(e.target.value)} sx={{ mb: 2 }} />
          {modalMsg && <Alert severity={modalMsg.includes('sucesso') ? 'success' : 'error'}>{modalMsg}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAssignSupervisor} disabled={modalLoading}>
            {modalLoading ? 'Salvando...' : 'Atribuir Supervisor'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Liberar temas */}
      <Dialog open={Boolean(temasDe)} onClose={() => setTemasDe(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Liberar temas — {temasDe?.name}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Libere só para empresa autorizada a oferecer o tema (por exemplo, financeira registrada no
            Banco Central) e que comprove que os contatos pediram para receber. A liberação tira a trava
            da Cetto, não a da Meta: o número continua sujeito a banimento.
          </Typography>
          <Stack>
            {temas.map((t) => (
              <FormControlLabel
                key={t.chave}
                control={(
                  <Checkbox
                    checked={temasMarcados.includes(t.chave)}
                    onChange={(e) => setTemasMarcados((atual) => (
                      e.target.checked ? [...atual, t.chave] : atual.filter((k) => k !== t.chave)
                    ))}
                  />
                )}
                label={t.nome}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemasDe(null)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarTemas} disabled={salvandoTemas}>
            {salvandoTemas ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Encerrar empresa */}
      <Dialog open={Boolean(encerrarDe)} onClose={() => !encerrando && setEncerrarDe(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{resumo ? `${resumo.empresa} foi encerrada` : `Encerrar ${encerrarDe?.name}`}</DialogTitle>
        <DialogContent dividers>
          {resumo ? (
            <Stack spacing={1.5}>
              <Alert severity="success">
                {resumo.linhas.toLocaleString('pt-BR')} registros e {resumo.arquivos.toLocaleString('pt-BR')} arquivos apagados.
              </Alert>
              <Typography variant="body2"><strong>Assinatura no Stripe:</strong> {resumo.stripe}</Typography>
              <Typography variant="body2" fontWeight={700}>Meta</Typography>
              {(resumo.meta || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">Nenhum chip conectado.</Typography>
              )}
              {(resumo.meta || []).map((m) => (
                <Typography key={`${m.chip}-${m.final}`} variant="body2">
                  {m.chip}{m.final ? ` (final ${m.final})` : ''}: {[
                    m.resultado,
                    m.webhooks && `webhooks ${m.webhooks}`,
                    m.numero && `número ${m.numero}`,
                  ].filter(Boolean).join(' · ')}
                </Typography>
              ))}
              <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                A conta do WhatsApp (WABA) e o portfólio são do cliente: a Cetto só corta o acesso dela.
                Se a WABA aparece no seu portfólio, remova o compartilhamento pelo Gerenciador de Negócios.
              </Alert>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Alert severity="error">
                Isto é <strong>sem volta</strong>. Na hora: desliga os webhooks e desregistra os números na Meta,
                cancela a assinatura no Stripe e apaga todos os usuários, contatos, conversas, templates,
                arquivos e configurações da empresa. Fica só um registro interno do encerramento.
              </Alert>
              <TextField
                label="Motivo (fica no registro interno)"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                multiline minRows={2} fullWidth
              />
              <TextField
                label={`Digite "${encerrarDe?.name || ''}" para confirmar`}
                value={confirmacao}
                onChange={(e) => setConfirmacao(e.target.value)}
                fullWidth
                error={Boolean(confirmacao) && !nomeConfere}
                autoComplete="off"
              />
              {erroEncerrar && <Alert severity="error">{erroEncerrar}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {resumo ? (
            <Button variant="contained" onClick={() => setEncerrarDe(null)}>Fechar</Button>
          ) : (
            <>
              <Button onClick={() => setEncerrarDe(null)} disabled={encerrando}>Cancelar</Button>
              <Button variant="contained" color="error" onClick={encerrar} disabled={!podeEncerrar}>
                {encerrando ? 'Encerrando...' : 'Encerrar e apagar tudo'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
}
